import React, { useState, useEffect } from 'react';

const RegionClock = ({ selectedRegion, compact = false }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [is24Hour, setIs24Hour] = useState(false);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Get timezone based on region
  const getTimezone = (region) => {
    // For Global view, use user's local timezone
    if (region.id === 'global') {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    
    const timezones = {
      // North America
      'north_america': 'America/New_York',
      'usa': 'America/New_York',
      'canada': 'America/Toronto',
      'mexico': 'America/Mexico_City',
      'greenland': 'America/Godthab',
      
      // US States (examples - major cities)
      'california': 'America/Los_Angeles',
      'new_york': 'America/New_York',
      'texas': 'America/Chicago',
      'florida': 'America/New_York',
      'illinois': 'America/Chicago',
      'washington': 'America/Los_Angeles',
      'nevada': 'America/Los_Angeles',
      'hawaii': 'Pacific/Honolulu',
      'alaska': 'America/Anchorage',
      
      // Canadian Provinces
      'ontario': 'America/Toronto',
      'quebec': 'America/Montreal',
      'british_columbia': 'America/Vancouver',
      'alberta': 'America/Edmonton',
      
      // Other Continents
      'europe': 'Europe/London',
      'asia': 'Asia/Tokyo',
      'africa': 'Africa/Cairo',
      'south_america': 'America/Sao_Paulo',
      'oceania': 'Australia/Sydney'
    };

    return timezones[region.id] || Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  // Format time based on 12hr/24hr preference
  const formatTime = (date, timezone, is24Hr) => {
    const options = {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: !is24Hr
    };

    return new Intl.DateTimeFormat('en-US', options).format(date);
  };

  // Get timezone abbreviation dynamically
  const getTimezoneAbbr = (timezone) => {
    try {
      const date = new Date();
      const tzParts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      }).formatToParts(date);
      
      const tzName = tzParts.find(part => part.type === 'timeZoneName');
      return tzName ? tzName.value : 'LOCAL';
    } catch (error) {
      return 'LOCAL';
    }
  };

  const timezone = getTimezone(selectedRegion);
  const formattedTime = formatTime(currentTime, timezone, is24Hour);
  const timezoneAbbr = getTimezoneAbbr(timezone);

  // Compact mode for mobile
  if (compact) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 10px',
        background: 'rgba(0, 255, 255, 0.08)',
        borderRadius: '6px'
      }}>
        <div style={{
          fontSize: '12px',
          fontWeight: '600',
          color: '#00FFFF',
          fontFamily: 'monospace'
        }}>
          {formattedTime}
        </div>
        <div style={{
          fontSize: '10px',
          color: '#64748b'
        }}>
          {timezoneAbbr}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '15px',
      padding: '8px 20px',
      background: 'rgba(0, 255, 255, 0.05)',
      border: '1px solid rgba(0, 255, 255, 0.3)',
      borderRadius: '8px',
      marginBottom: '10px'
    }}>
      {/* Region Name */}
      <div style={{
        fontSize: '14px',
        fontWeight: '600',
        color: '#FF00FF',
        filter: 'drop-shadow(0 0 5px rgba(255, 0, 255, 0.6))'
      }}>
        {selectedRegion.name}
      </div>

      {/* Separator */}
      <div style={{
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        background: '#00FFFF',
        boxShadow: '0 0 5px rgba(0, 255, 255, 0.8)'
      }}></div>

      {/* Time Display */}
      <div style={{
        fontSize: '16px',
        fontWeight: '700',
        color: '#00FFFF',
        fontFamily: 'monospace',
        letterSpacing: '1px',
        filter: 'drop-shadow(0 0 8px rgba(0, 255, 255, 0.8))'
      }}>
        {formattedTime}
      </div>

      {/* Timezone */}
      <div style={{
        fontSize: '12px',
        fontWeight: '500',
        color: '#94a3b8',
        paddingLeft: '5px'
      }}>
        {timezoneAbbr}
      </div>

      {/* 12/24 Hour Toggle */}
      <div
        onClick={() => setIs24Hour(!is24Hour)}
        style={{
          padding: '4px 10px',
          background: 'rgba(255, 0, 255, 0.1)',
          border: '1px solid rgba(255, 0, 255, 0.3)',
          borderRadius: '5px',
          fontSize: '11px',
          fontWeight: '600',
          color: '#FF00FF',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 0, 255, 0.2)';
          e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 0, 255, 0.1)';
          e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.3)';
        }}
      >
        <span>{is24Hour ? '24HR' : '12HR'}</span>
        <span style={{ fontSize: '9px' }}>▼</span>
      </div>
    </div>
  );
};

export default RegionClock;
